/* import __COLOCATED_TEMPLATE__ from './conversation-details.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationAttributeSummary from 'embercom/objects/inbox/conversation-attribute-summary';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { task } from 'ember-concurrency-decorators';
import type InboxState from 'embercom/services/inbox-state';
// @ts-ignore
import { trackedReset } from 'tracked-toolbox';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import { type SidebarSectionComponentArgs } from 'embercom/lib/inbox2/sidebar-types';
import ConditionalAttributesEvaluator from 'embercom/services/conditional-attributes-evaluator';
import { getOwner } from '@ember/application';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import { CompanySelectionUtils } from 'embercom/mixins/company-selection';
import type CompanySummary from 'embercom/objects/inbox/company-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type RegionService from 'embercom/services/region-service';

interface Args {
  conversation: Conversation;
  conversationAttributeDescriptors: ConversationAttributeDescriptor[];
  isOpen: boolean;
  name: string;
  onOpenSectionChange: () => unknown;
  autofocusTitle?: boolean;
  onConversationOrChecklistSelected?: () => void;
  previewOnly?: boolean;
  isPreviewingConversation?: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: SidebarSectionComponentArgs<Args>;
}

const EXCLUDED_EDITABLE_ATTRIBUTE_IDS = ['brand', 'ai_title'];

export default class ConversationDetails extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare inboxApi: InboxApi;
  @service declare regionService: RegionService;

  @tracked visibleToUser = true;
  @trackedReset('args.conversation.id') titleInput = this.args.conversation.title || '';
  @trackedReset('args.conversation.id') formSubmitted = false;
  @tracked searchAttributeValue = '';
  @tracked associatedCompanyWasDeleted = false;

  get conditionalAttributesEvaluator() {
    return new ConditionalAttributesEvaluator({
      conversation: this.args.conversation,
      descriptors: this.args.conversationAttributeDescriptors,
      owner: getOwner(this),
    });
  }

  conversationAttributesDescriptors() {
    return this.conditionalAttributesEvaluator.updateVisibility();
  }

  get visibleConversationAttributeDescriptors() {
    return this.conversationAttributesDescriptors()?.filter(
      (descriptor: ConversationAttributeDescriptor) =>
        !descriptor.archived &&
        !descriptor.hideFromInboxSidebar &&
        descriptor.isVisibleToTeamId(this.args.conversation.teamAssignee?.id),
    );
  }

  get brandDescriptor() {
    return this.visibleConversationAttributeDescriptors?.find(
      (descriptor: ConversationAttributeDescriptor) => descriptor.id === 'brand',
    );
  }

  get aiTitleDescriptor() {
    return this.visibleConversationAttributeDescriptors?.find(
      (descriptor: ConversationAttributeDescriptor) => descriptor.id === 'ai_title',
    );
  }

  get shouldShowAiTitle() {
    // Show for EU and AU regions without feature flag check
    if (this.regionService.isEU || this.regionService.isAU) {
      return true;
    }
    // For US region, check the feature flag
    if (this.regionService.isUS) {
      return this.session.workspace.isFeatureEnabled('team-messenger-ai-titles');
    }
    return false;
  }

  get conversationAttributesById() {
    return this.args.conversation.attributes?.reduce(
      (byIds, attribute) => {
        byIds[attribute.descriptor.id] = attribute;
        return byIds;
      },
      {} as Record<string, ConversationAttributeSummary>,
    );
  }

  get hasCreatedByValue() {
    return this.conversationAttributesById?.['created_by_teammate_id']?.value;
  }

  get showConversationSubject(): boolean {
    return !this.args.conversation.isTicket;
  }

  get header(): string {
    return this.intl.t(
      'inbox.conversation-details-sidebar.section-headings.conversation-attributes',
    );
  }

  private trackLanguageChange(attribute: ConversationAttributeSummary) {
    if (attribute.descriptor?.id === 'language') {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'conversation_attribute',
        place: 'conversation_details_sidebar',
        metadata: {
          conversation_id: this.args.conversation?.id,
          attribute_id: attribute.descriptor.id,
          attribute_current_value: attribute.value,
          attribute_previous_value: attribute.valueBeforeUpdate,
        },
      });
    }
  }

  @action async onUpdateAttribute(attribute: ConversationAttributeSummary) {
    await this.conditionalAttributesEvaluator.updateAttribute(attribute, this.args.conversation);
    this.trackLanguageChange(attribute);
  }

  @action toggleVisibleToUser() {
    this.visibleToUser = !this.visibleToUser;
  }

  @task({ keepLatest: true })
  *updateConversationTitle() {
    let previousTitle = this.args.conversation.title;
    let newTitle = this.titleInput?.trim();

    if (newTitle === previousTitle) {
      return;
    }
    yield this.inboxState.updateConversationTitle(this.args.conversation, newTitle, previousTitle);
  }

  get filteredAttributes() {
    let attributes = this.allAttributes;

    if (this.searchAttributeValue.trim()) {
      attributes = attributes.filter((descriptor: ConversationAttributeDescriptor) => {
        return (
          descriptor &&
          descriptor.name
            .toLocaleLowerCase()
            .includes(this.searchAttributeValue.toLocaleLowerCase())
        );
      });
    }

    return [
      ...attributes
        .filter((attr: ConversationAttributeDescriptor) => !attr.isSystemDefined)
        .sortBy('order'),
      ...attributes.filter((attr: ConversationAttributeDescriptor) => attr.isSystemDefined),
    ];
  }

  get allAttributes() {
    return [...this.primaryAttributes, ...this.secondaryAttributes];
  }

  get primaryAttributes() {
    let attributes = this.visibleConversationAttributeDescriptors.filter(
      (descriptor: ConversationAttributeDescriptor) => {
        return !descriptor.isSecondaryAttribute;
      },
    );

    return [
      ...attributes
        .filter((attr: ConversationAttributeDescriptor) => !attr.isSystemDefined)
        .sortBy('order'),
      ...attributes.filter((attr: ConversationAttributeDescriptor) => attr.isSystemDefined),
    ];
  }

  get secondaryAttributes() {
    return this.visibleConversationAttributeDescriptors.filter(
      (descriptor: ConversationAttributeDescriptor) => {
        return descriptor.isSecondaryAttribute;
      },
    );
  }

  get userCompanies(): Array<CompanySummary> {
    return CompanySelectionUtils.getUserCompanies(this.args.conversation);
  }

  get shouldShowCompanySelect(): boolean {
    // Hide company select for tickets

    if (!this.args.conversation.id || this.args.conversation.isTicket) {
      return false;
    }

    return true;
  }

  get shouldShowCompanyDropdown(): boolean {
    // Hide company dropdown if no companies are associated with the conversation
    return CompanySelectionUtils.shouldShowCompanySelect(
      this.userCompanies,
      this.hasAssociatedCompany,
    );
  }
  get hasAssociatedCompany(): boolean {
    return CompanySelectionUtils.hasAssociatedCompany(this.args.conversation);
  }

  shouldShowEditableAttribute(descriptorId: string | number): boolean {
    return !EXCLUDED_EDITABLE_ATTRIBUTE_IDS.includes(String(descriptorId));
  }

  @action
  updateAssociatedCompanyWasDeleted(deleted: boolean): void {
    this.associatedCompanyWasDeleted = deleted;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::ConversationDetails': typeof ConversationDetails;
    'inbox2/conversation-details-sidebar/conversation-details': typeof ConversationDetails;
  }
}
